export const RATIO_GLOSSARY_FALLBACK = [
  {
    key: "sharpe",
    title: "Sharpe Ratio",
    description:
      "Measures the excess return (above the risk-free rate) earned per unit of total portfolio volatility. Formula: (Portfolio Return − Risk-Free Rate) ÷ Standard Deviation of Returns.",
    interpretation:
      "Interpretation: <0 = poor; 0–0.5 = below average; 0.5–1.0 = acceptable; 1.0–2.0 = good; >2.0 = excellent",
    span: 4,
  },
  {
    key: "sortino",
    title: "Sortino Ratio",
    description:
      "A refinement of the Sharpe Ratio that only penalises downside volatility (negative deviations below the target return), not total volatility. Formula: (Return − Target Return) ÷ Downside Deviation.",
    interpretation:
      "Interpretation: Higher is better. More meaningful than Sharpe for portfolios with asymmetric return distributions.",
    span: 4,
  },
  {
    key: "beta",
    title: "Beta (β)",
    description:
      "Measures the portfolio's sensitivity to benchmark market movements. A Beta of 1.0 means the portfolio moves in line with the benchmark. Beta >1 = more volatile; Beta <1 = more defensive.",
    interpretation:
      "Benchmark: Nifty 50. This portfolio's Beta of 0.94 indicates marginally defensive market sensitivity.",
    span: 4,
  },
  {
    key: "calmar",
    title: "Calmar Ratio",
    description:
      "Compares annualised return to maximum drawdown. A measure of return earned per unit of tail risk taken. Formula: Annualised Return ÷ |Maximum Drawdown|. Useful for evaluating downside-adjusted performance.",
    interpretation:
      "Interpretation: <0.5 = poor; 0.5–1.0 = average; >1.0 = good; >3.0 = excellent. This portfolio's 0.17 signals inadequate return for the drawdown risk taken.",
    span: 4,
  },
  {
    key: "volatility",
    title: "Annualised Volatility (σ)",
    description:
      "The standard deviation of daily returns, scaled to an annual figure (typically multiplied by √252). Represents the degree of return fluctuation. Higher volatility = higher risk, but also potentially higher return.",
    interpretation:
      "Interpretation: Nifty 500 avg ~18%. This portfolio at 22.8% is above benchmark, driven by concentrated positions in mid-caps and Tata Elxsi.",
    span: 4,
  },
  {
    key: "liquidity",
    title: "Liquidity",
    description:
      "0 stocks out of 5 face Liquidity Risk in terms of their Average Traded Value. Weightage wise 0% of the value of the portfolio may have liquidity issues.",
    interpretation: null,
    span: 4,
    lightBody: true,
  },
  {
    key: "trackingError",
    title: "Tracking Error",
    description:
      "The standard deviation of the difference between portfolio returns and benchmark returns. Measures how consistently the portfolio deviates from its benchmark. Used in the Information Ratio. Higher Tracking Error = more active management.",
    interpretation:
      "Typical range: Passive ETFs 0–0.5%; Active funds 3–10%; Concentrated stock portfolios 8–15%.",
    span: 6,
  },
  {
    key: "maxDrawdown",
    title: "Maximum Drawdown (MDD)",
    description:
      "The largest peak-to-trough decline in portfolio value over a given period, expressed as a percentage. Captures the worst-case loss an investor would have experienced at any point in time.",
    interpretation:
      "Interpretation: Smaller (less negative) is better. An MDD of -18.4% means the portfolio fell 18.4% from its peak at some point in the measured period.",
    span: 6,
  },
];

const findMetricValue = (metrics = [], ...needles) => {
  const list = Array.isArray(metrics) ? metrics : [];
  for (const needle of needles) {
    const match = list.find((m) => {
      const key = String(m?.key || "").toLowerCase();
      const label = String(m?.label || "").toLowerCase();
      return key.includes(needle) || label.includes(needle);
    });
    if (match?.value != null && match.value !== "-" && match.value !== "") {
      return match.value;
    }
  }
  return null;
};

const formatNum = (value, digits = 2) => {
  if (value == null || value === "") return null;
  const num = Number(String(value).replace(/%/g, "").replace(/,/g, "").trim());
  if (!Number.isFinite(num)) return String(value);
  return Number.isInteger(num) ? String(num) : num.toFixed(digits);
};

/**
 * Build glossary cards — Figma copy with live portfolio numbers when available.
 */
export const buildRatioGlossary = ({
  metrics = [],
  beta,
  liquiditySynopsis,
  stockCount,
} = {}) => {
  const sharpe = findMetricValue(metrics, "sharpe");
  const sortino = findMetricValue(metrics, "sortino");
  const calmar = findMetricValue(metrics, "calmar", "calmer");
  const volatility = findMetricValue(metrics, "volatility", "vol");
  const mdd = findMetricValue(metrics, "drawdown", "mdd", "maximum");
  const tracking = findMetricValue(metrics, "tracking");

  const betaVal =
    beta != null && beta !== "" && beta !== "-"
      ? formatNum(beta, 2)
      : findMetricValue(metrics, "beta");

  const illiquidCount =
    liquiditySynopsis?.illiquid_count ??
    liquiditySynopsis?.illiquid ??
    liquiditySynopsis?.illiquidStocks ??
    null;
  const liquidTotal =
    stockCount ??
    liquiditySynopsis?.total_stocks ??
    liquiditySynopsis?.stock_count ??
    null;
  const illiquidWeight =
    liquiditySynopsis?.illiquid_weightage ??
    liquiditySynopsis?.illiquid_weight ??
    liquiditySynopsis?.weightage_illiquid ??
    null;

  return RATIO_GLOSSARY_FALLBACK.map((card) => {
    const next = { ...card };

    if (card.key === "beta" && betaVal != null) {
      const num = Number(betaVal);
      const stance =
        Number.isFinite(num) && num < 1
          ? "marginally defensive market sensitivity"
          : Number.isFinite(num) && num > 1
            ? "higher-than-market sensitivity"
            : "market-aligned sensitivity";
      next.interpretation = `Benchmark: Nifty 50. This portfolio's Beta of ${betaVal} indicates ${stance}.`;
    }

    if (card.key === "calmar" && calmar != null) {
      const num = Number(String(calmar).replace(/%/g, ""));
      const note =
        Number.isFinite(num) && num < 0.5
          ? "signals inadequate return for the drawdown risk taken"
          : Number.isFinite(num) && num < 1
            ? "is average versus drawdown risk taken"
            : "shows solid return relative to drawdown risk";
      next.interpretation = `Interpretation: <0.5 = poor; 0.5–1.0 = average; >1.0 = good; >3.0 = excellent. This portfolio's ${formatNum(calmar, 2)} ${note}.`;
    }

    if (card.key === "volatility" && volatility != null) {
      const num = Number(String(volatility).replace(/%/g, ""));
      const vsBench =
        Number.isFinite(num) && num > 18
          ? "is above benchmark"
          : Number.isFinite(num) && num < 15
            ? "is below benchmark"
            : "is near the Nifty 500 average";
      next.interpretation = `Interpretation: Nifty 500 avg ~18%. This portfolio at ${formatNum(volatility, 1)}% ${vsBench}.`;
    }

    if (card.key === "liquidity") {
      if (illiquidCount != null || liquidTotal != null || illiquidWeight != null) {
        const stocks = illiquidCount ?? 0;
        const total = liquidTotal ?? "—";
        const weight = illiquidWeight != null ? formatNum(illiquidWeight, 1) : "0";
        next.description = `${stocks} stocks out of ${total} face Liquidity Risk in terms of their Average Traded Value. Weightage wise ${weight}% of the value of the portfolio may have liquidity issues.`;
      }
    }

    if (card.key === "maxDrawdown" && mdd != null) {
      const abs = Math.abs(Number(String(mdd).replace(/%/g, "")));
      const display = Number.isFinite(abs) ? `-${formatNum(abs, 1)}%` : String(mdd);
      next.interpretation = `Interpretation: Smaller (less negative) is better. An MDD of ${display} means the portfolio fell ${Number.isFinite(abs) ? `${formatNum(abs, 1)}%` : mdd} from its peak at some point in the measured period.`;
    }

    if (card.key === "sharpe" && sharpe != null) {
      next.interpretation = `Interpretation: <0 = poor; 0–0.5 = below average; 0.5–1.0 = acceptable; 1.0–2.0 = good; >2.0 = excellent. This portfolio: ${formatNum(sharpe, 2)}.`;
    }

    if (card.key === "sortino" && sortino != null) {
      next.interpretation = `Interpretation: Higher is better. More meaningful than Sharpe for portfolios with asymmetric return distributions. This portfolio: ${formatNum(sortino, 2)}.`;
    }

    if (card.key === "trackingError" && tracking != null) {
      next.interpretation = `Typical range: Passive ETFs 0–0.5%; Active funds 3–10%; Concentrated stock portfolios 8–15%. This portfolio: ${formatNum(tracking, 2)}%.`;
    }

    return next;
  });
};
